import React, { useState, useEffect } from 'react';
import { Loader2, AlertCircle, BarChart2, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { formatQuantity } from 'core/utils/units';
import { getLagerSupplyDemand } from 'core/api/apiService';

/**
 * AtpBlock — ATP/Verfuegbarkeit (MD04-Stil) fuer ein Material/Werk (Phase 4c).
 *
 * FARBREGEL: Gruen/Rot ist AUSSCHLIESSLICH richtungsbezogen.
 *   Zugang  = gruen  (+ Vorzeichen)
 *   Abgang  = rot    (- Vorzeichen)
 * Das ist KEINE Bestands-Ampel. Verfuegbar-Netto wird NEUTRAL/hervorgehoben
 * dargestellt — kein rot/gruen-Schwellwert.
 *
 * Props:
 *   matnr   string   Materialnummer
 *   werk    string   Werk (Pflicht fuer den Endpunkt)
 *   user    Object   Session-User (Token)
 *   meins   string   Mengeneinheit (optional, fuer formatQuantity)
 */
const AtpBlock = ({ matnr, werk, user, meins }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [expanded, setExpanded] = useState(false);

  // ---- Daten laden ----
  useEffect(() => {
    if (!matnr || !werk) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    getLagerSupplyDemand(user, matnr, werk)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [matnr, werk]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Styles ----
  const cellMuted    = isDarkMode ? 'text-white/45' : 'text-slate-400';
  const cellVal      = isDarkMode ? 'text-white/85' : 'text-slate-800';
  const sectionTitle = `text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`;
  const dividerCls   = `border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`;

  // Richtungsfarben (KEIN Bestands-Schwellwert — nur Zugang/Abgang)
  const clsZugang = isDarkMode ? 'text-emerald-400' : 'text-emerald-600';
  const clsAbgang = isDarkMode ? 'text-red-400'     : 'text-red-500';

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const fmtMenge = (val) => formatQuantity(val, meins);

  // ---- 3-State: Loading ----
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4">
        <Loader2 className={`w-4 h-4 animate-spin ${isDarkMode ? 'text-brand' : 'text-brand'}`} />
        <span className={`text-xs ${cellMuted}`}>{t('lm.atpLoading')}</span>
      </div>
    );
  }

  // ---- 3-State: Error ----
  if (error) {
    return (
      <div className="flex items-center gap-2 py-3">
        <AlertCircle className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
        <span className={`text-xs ${cellMuted}`}>{t('lm.atpError')}: {error}</span>
      </div>
    );
  }

  // ---- 3-State: Empty ----
  if (!data) {
    return (
      <p className={`text-xs ${cellMuted} py-2`}>{t('lm.atpEmpty')}</p>
    );
  }

  const zugaenge = Array.isArray(data.zugaenge) ? data.zugaenge : [];
  const abgaenge = Array.isArray(data.abgaenge) ? data.abgaenge : [];

  // Termine sortieren (aufsteigend)
  const sortByTermin = (arr) =>
    [...arr].sort((a, b) => new Date(a.termin) - new Date(b.termin));

  return (
    <div>
      {/* ---- Kopfzeile mit Icon + Titel ---- */}
      <div className="flex items-center gap-2 mb-3">
        <BarChart2 className={`w-4 h-4 ${isDarkMode ? 'text-brand/70' : 'text-brand'}`} />
        <p className={sectionTitle}>{t('lm.atpTitle')}</p>
      </div>

      {/* ---- KPI-Zusammenfassung (immer sichtbar) ---- */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">

        {/* Bestand */}
        <div className={`rounded-lg px-3 py-2 ${isDarkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
          <p className={`text-xs mb-0.5 ${cellMuted}`}>{t('lm.atpBestand')}</p>
          <p className={`text-sm font-semibold ${cellVal}`}>
            {fmtMenge(data.bestand)}{meins ? ` ${meins}` : ''}
          </p>
        </div>

        {/* Zugaenge — RICHTUNGSFARBE gruen (Zugang), keine Bestands-Ampel */}
        <div className={`rounded-lg px-3 py-2 ${isDarkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
          <p className={`text-xs mb-0.5 ${cellMuted}`}>{t('lm.atpZugaenge')}</p>
          <p className={`text-sm font-semibold ${clsZugang}`}>
            +{fmtMenge(data.summe_zugaenge)}{meins ? ` ${meins}` : ''}
          </p>
        </div>

        {/* Abgaenge — RICHTUNGSFARBE rot (Abgang), keine Bestands-Ampel */}
        <div className={`rounded-lg px-3 py-2 ${isDarkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
          <p className={`text-xs mb-0.5 ${cellMuted}`}>{t('lm.atpAbgaenge')}</p>
          <p className={`text-sm font-semibold ${clsAbgang}`}>
            -{fmtMenge(data.summe_abgaenge)}{meins ? ` ${meins}` : ''}
          </p>
        </div>

        {/* Verfuegbar Netto — NEUTRAL/hervorgehoben, kein rot/gruen-Schwellwert */}
        <div className={`rounded-lg px-3 py-2 ${
          isDarkMode ? 'bg-brand/15 ring-1 ring-brand/30' : 'bg-brand/8 ring-1 ring-brand/20'
        }`}>
          <p className={`text-xs mb-0.5 ${cellMuted}`}>{t('lm.atpVerfuegbarNetto')}</p>
          <p className={`text-sm font-bold ${isDarkMode ? 'text-white/90' : 'text-slate-900'}`}>
            {fmtMenge(data.verfuegbar_netto)}{meins ? ` ${meins}` : ''}
          </p>
        </div>

      </div>

      {/* ---- Aufklapp-Schalter ---- */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={`flex items-center gap-1.5 text-xs transition-colors ${
          isDarkMode
            ? 'text-brand/70 hover:text-brand'
            : 'text-brand/80 hover:text-brand'
        }`}
        aria-expanded={expanded}
      >
        {expanded
          ? <ChevronUp className="w-3.5 h-3.5" />
          : <ChevronDown className="w-3.5 h-3.5" />
        }
        {expanded ? t('lm.atpCollapse') : t('lm.atpExpand')}
      </button>

      {/* ---- MD04-Detailliste (aufklappbar) ---- */}
      {expanded && (
        <div className={`mt-3 pt-3 ${dividerCls} grid grid-cols-1 xl:grid-cols-2 gap-4`}>

          {/* Zugaenge */}
          <div>
            <p className={`text-xs font-semibold mb-2 ${clsZugang}`}>
              {t('lm.atpZugaengeTitle')}
            </p>
            {zugaenge.length === 0 ? (
              <p className={`text-xs ${cellMuted}`}>{t('lm.atpZugaengeEmpty')}</p>
            ) : (
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className={`border-b ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                    <th className={`text-left pb-1.5 pr-2 font-semibold ${cellMuted}`}>{t('lm.atpElementTyp')}</th>
                    <th className={`text-left pb-1.5 pr-2 font-semibold ${cellMuted}`}>{t('lm.atpBeleg')}</th>
                    <th className={`text-right pb-1.5 pr-2 font-semibold ${cellMuted}`}>{t('lm.atpMenge')}</th>
                    <th className={`text-left pb-1.5 font-semibold ${cellMuted}`}>{t('lm.atpTermin')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortByTermin(zugaenge).map((row, idx) => (
                    <tr
                      key={`z-${row.beleg ?? idx}-${idx}`}
                      className={`border-b ${isDarkMode ? 'border-white/5' : 'border-slate-50'}`}
                    >
                      <td className={`py-1.5 pr-2 ${cellMuted}`}>{row.element_typ ?? '—'}</td>
                      <td className={`py-1.5 pr-2 font-mono ${cellVal}`}>{row.beleg ?? '—'}</td>
                      {/* Menge: RICHTUNGSFARBE gruen (Zugang) */}
                      <td className={`py-1.5 pr-2 text-right font-medium ${clsZugang}`}>
                        +{fmtMenge(row.menge)}
                      </td>
                      <td className={`py-1.5 ${cellMuted}`}>{formatDate(row.termin)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Abgaenge */}
          <div>
            <p className={`text-xs font-semibold mb-2 ${clsAbgang}`}>
              {t('lm.atpAbgaengeTitle')}
            </p>
            {abgaenge.length === 0 ? (
              <p className={`text-xs ${cellMuted}`}>{t('lm.atpAbgaengeEmpty')}</p>
            ) : (
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className={`border-b ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                    <th className={`text-left pb-1.5 pr-2 font-semibold ${cellMuted}`}>{t('lm.atpElementTyp')}</th>
                    <th className={`text-left pb-1.5 pr-2 font-semibold ${cellMuted}`}>{t('lm.atpBeleg')}</th>
                    <th className={`text-right pb-1.5 pr-2 font-semibold ${cellMuted}`}>{t('lm.atpMenge')}</th>
                    <th className={`text-left pb-1.5 font-semibold ${cellMuted}`}>{t('lm.atpTermin')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortByTermin(abgaenge).map((row, idx) => (
                    <tr
                      key={`a-${row.beleg ?? idx}-${idx}`}
                      className={`border-b ${isDarkMode ? 'border-white/5' : 'border-slate-50'}`}
                    >
                      <td className={`py-1.5 pr-2 ${cellMuted}`}>{row.element_typ ?? '—'}</td>
                      <td className={`py-1.5 pr-2 font-mono ${cellVal}`}>{row.beleg ?? '—'}</td>
                      {/* Menge: RICHTUNGSFARBE rot (Abgang) */}
                      <td className={`py-1.5 pr-2 text-right font-medium ${clsAbgang}`}>
                        -{fmtMenge(row.menge)}
                      </td>
                      <td className={`py-1.5 ${cellMuted}`}>{formatDate(row.termin)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default AtpBlock;
